import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import CartArticle from "@Components/molecules/cart/CartArticle";
import CartCheckoutTotal from "@Components/molecules/cart/CartCheckoutTotal";
import CartFooter from "@Components/molecules/cart/CartFooter";
import CartRemoveProductModal from "@Components/molecules/cart/CartRemoveProductModal";
import PromosModal from "@Components/molecules/cart/PromosModal";
import useCartContext from "@Hooks/useCartContext";
import CheerzApiCart from "@Types/api/cart";
import { Article, Step } from "@Types/cart";
import { convertFromApiToCartContent, convertFromCartContentToApi } from "@Utils/cart/cart";
import {
  convertToCheckoutStepProperties,
  convertToOrderUpdatedProperties,
  setDataLayerTrack,
} from "@Utils/track";
import "./style.scss";

interface Props {
  articles: Article[];
  disabled: boolean;
  onConfirmOrder: () => void;
  onRemoveArticle: (removeArticle: Article) => void;
  onUpdatePromoCode: (promoCode: string | undefined) => void;
  promoCodeErrorMessage: string | undefined;
}

interface ContainerProps {
  disabled: boolean;
  onChangeStep: (step: Step) => void;
  onUpdatePromoCode: (promoCode: string | undefined) => void;
  promoCodeErrorMessage: string | undefined;
}

export const CartCheckoutStep: FunctionComponent<Props> = ({
  articles,
  disabled,
  onConfirmOrder,
  onRemoveArticle,
  onUpdatePromoCode,
  promoCodeErrorMessage,
}) => {
  const intl = useIntl();
  const [removeArticle, setRemoveArticle] = useState<Article | null>(null);
  const [showPromoModal, setShowPromoModal] = useState<boolean>(false);
  const [showRemoveArticleModal, setShowRemoveArticleModal] = useState<boolean>(false);

  const handleConfirmRemoveArticle = useCallback(() => {
    if (removeArticle) {
      onRemoveArticle(removeArticle);
    }
    handleHideRemoveArticleModal();
  }, [removeArticle, onRemoveArticle]);

  const handleDismissPromoModal = () => {
    setShowPromoModal(false);
  };

  const handleHideRemoveArticleModal = () => {
    setShowRemoveArticleModal(false);
    setRemoveArticle(null);
  };

  const handleRemoveArticle = (article: Article) => {
    setRemoveArticle(article);
    setShowRemoveArticleModal(true);
  };

  const handleClickBannerPromo = () => {
    setDataLayerTrack("Cart Promo Codes List Clicked");
    setShowPromoModal(true);
  };

  const onSelectPromo = (promoCode: string) => {
    onUpdatePromoCode(promoCode);
    handleDismissPromoModal();
  };

  const handleClickCategoriesLink = () => {
    setDataLayerTrack("Product List Clicked", {
      categoryPageTag: "all",
      linkPosition: "Checkout",
    });
    window.location.href = "categories/all";
  };

  const cartArticles = articles.map((article) => {
    return (
      <CartArticle
        key={article.id}
        article={article}
        disabled={disabled}
        onRemove={handleRemoveArticle}
      />
    );
  });

  return (
    <div className="CartCheckoutStep">
      {cartArticles}
      <CartRemoveProductModal
        display={showRemoveArticleModal}
        onDismiss={handleHideRemoveArticleModal}
        onRemove={handleConfirmRemoveArticle}
      />

      <div className="CartCheckoutStep-content-line">
        <div className="CartCheckoutStep-link" onClick={handleClickCategoriesLink}>
          + {intl.formatMessage({ id: "cheerz_Cart_new_product" })}
        </div>
      </div>

      <CartCheckoutTotal
        disabled={disabled}
        onClickBanner={handleClickBannerPromo}
        onSubmitOrder={onConfirmOrder}
        onUpdatePromoCode={onUpdatePromoCode}
        promoCodeErrorMessage={promoCodeErrorMessage}
      />
      <PromosModal
        display={showPromoModal}
        onDismiss={handleDismissPromoModal}
        onSelectPromo={onSelectPromo}
      />

      <div className="CartCheckoutStep-content-line">
        <CartFooter />
      </div>
    </div>
  );
};

const CartCheckoutStepContainer: FunctionComponent<ContainerProps> = ({
  disabled,
  onChangeStep,
  onUpdatePromoCode,
  promoCodeErrorMessage,
}) => {
  const { cart, configuration, dispatch } = useCartContext();

  useEffect(() => {
    setDataLayerTrack("Checkout Step Viewed", convertToCheckoutStepProperties("Checkout", 1, cart));
  }, []);

  const handleRemoveArticle = (removeArticle: Article) => {
    dispatch({
      disabled: true,
      type: "setDisabledCart",
    });
    const articles = cart.articles.filter((article) => article.id !== removeArticle.id);
    const updatedCart = { ...cart, articles };
    const updatedCartJson = convertFromCartContentToApi(updatedCart);

    configuration.apiProviders.cheerz
      .saveCart(updatedCartJson)
      .then((cartResponse: CheerzApiCart) => {
        dispatch({
          cart: {
            ...convertFromApiToCartContent(cartResponse),
          },
          type: "setSavedCart",
        });
        setDataLayerTrack("Order Updated", convertToOrderUpdatedProperties(cart, "Checkout"));
        setDataLayerTrack("Product Removed", {
          articleCategory: removeArticle.product.statProductGroupTag,
          articleId: removeArticle.id,
          articleName: removeArticle.product.name,
          articlePrice: removeArticle.total.cents,
          articleQuantity: removeArticle.quantity,
          cartId: cart.orderNumber,
        });
      })
      .catch(() => {
        return;
      });
  };

  const handleSubmitOrder = () => {
    if (configuration) {
      dispatch({
        disabled: true,
        type: "setDisabledCart",
      });

      const updatedCartJson = convertFromCartContentToApi(cart);

      configuration.apiProviders.cheerz
        .saveCart(updatedCartJson)
        .then((cartResponse: CheerzApiCart) => {
          dispatch({
            cart: {
              ...convertFromApiToCartContent(cartResponse),
            },
            type: "setSavedCart",
          });
          setDataLayerTrack(
            "Checkout Step Completed",
            convertToCheckoutStepProperties("Checkout", 1, cart)
          );
          onChangeStep("address");
        })
        .catch(() => {
          return;
        });
    }
  };

  return (
    <CartCheckoutStep
      articles={cart.articles}
      disabled={disabled}
      onConfirmOrder={handleSubmitOrder}
      onRemoveArticle={handleRemoveArticle}
      onUpdatePromoCode={onUpdatePromoCode}
      promoCodeErrorMessage={promoCodeErrorMessage}
    />
  );
};

export default CartCheckoutStepContainer;
